import React, { useState, useEffect } from 'react';
import {
  Card, Steps, Button, Input, Select, Form, Typography, Space,
  Tag, Divider, Spin, Alert, Row, Col, InputNumber, Tooltip,
} from 'antd';
import { App } from 'antd';
import {
  ShopOutlined, RocketOutlined, RobotOutlined, CheckCircleOutlined,
  PlusOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined,
  HeartOutlined, StarOutlined,
} from '@ant-design/icons';
import apiClient from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const TONE_OPTIONS = [
  { value: 'friendly',     label: '😊 Friendly & Warm',     desc: 'Like a helpful friend — casual and approachable' },
  { value: 'professional', label: '💼 Professional',         desc: 'Clear, polite and respectful of customer time' },
  { value: 'luxury',       label: '✨ Luxury & Premium',     desc: 'Refined and attentive — customers are guests' },
  { value: 'playful',      label: '🎉 Fun & Playful',        desc: 'Energetic and upbeat — light use of emojis' },
];

const BUSINESS_TYPES = [
  'D2C', 'Hotel', 'SaaS', 'Clinic', 'Legal', 'Restaurant',
  'Education', 'Real Estate', 'Retail', 'Other',
];

const ROLE_COLORS = {
  customer_support:      '#52c41a',
  product_advisor:       '#1890ff',
  order_subscription:    '#722ed1',
  booking:               '#13c2c2',
  concierge:             '#fa8c16',
  sales_demo:            '#eb2f96',
  technical_support:     '#faad14',
  default:               '#8c8c8c',
};

const getStorageKey = (accountId) => `onboarding_draft_${accountId}`;

const load = (accountId, key, fallback) => {
  try {
    const saved = localStorage.getItem(getStorageKey(accountId));
    if (!saved) return fallback;
    const parsed = JSON.parse(saved);
    return parsed[key] !== undefined ? parsed[key] : fallback;
  } catch { return fallback; }
};

const Onboarding = ({ account, onComplete }) => {
  const { message } = App.useApp();
  const accountId = account?._id || 'guest';
  const STORAGE_KEY = getStorageKey(accountId);

  const [current, setCurrent]           = useState(() => load(accountId, 'step', 0));
  const [generating, setGenerating]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [agents, setAgents]             = useState(() => load(accountId, 'agents', []));
  const [ccConfig, setCcConfig]         = useState(() => load(accountId, 'ccConfig', null));
  const [editingAgent, setEditingAgent] = useState(null);

  // Form state — restored from localStorage on mount
  const [biz, setBiz] = useState(() => load(accountId, 'biz', {
    businessName:        '',
    businessType:        '',
    businessDescription: '',
    brandTone:           'friendly',
    goal:                '',
  }));
  const [products, setProducts] = useState(() => load(accountId, 'products', [
    { name: '', description: '', price: '', currency: '₹', unit: 'per pack' },
  ]));

  // Clean up legacy global draft on mount
  useEffect(() => {
    localStorage.removeItem('onboarding_draft');
  }, []);

  // On mount — try to restore from DB (cross-device), fallback to localStorage
  useEffect(() => {
    console.log('[Onboarding] Mounting — checking DB for saved draft…');
    apiClient.get('/onboarding/draft').then(res => {
      const draft = res.data?.draft;
      if (!draft || !draft.biz) {
        console.log('[Onboarding] No DB draft found — starting fresh (localStorage may have partial state)');
        return;
      }
      console.log('[Onboarding] ✅ Draft restored from DB:', {
        step: draft.step,
        business: draft.biz?.businessName,
        agentsCount: draft.agents?.length || 0,
        savedAt: draft.savedAt,
      });

      // If draft is already at step 3 (Go Live screen), onboarding was completed
      // but onboardingCompleted flag may not have propagated — auto-redirect
      if (draft.step >= 3) {
        console.log('[Onboarding] Draft is at step 3 (Go Live) — onboarding was already completed. Auto-redirecting…');
        localStorage.removeItem('onboarding_draft');
        apiClient.post('/onboarding/save-draft', {}).catch(() => {});
        setTimeout(() => onComplete?.(), 500);
        return;
      }

      if (draft.biz)            setBiz(draft.biz);
      if (draft.products)       setProducts(draft.products);
      if (draft.agents?.length) { setAgents(draft.agents); setCcConfig(draft.ccConfig || null); }
      if (draft.step)           setCurrent(draft.step);
    }).catch(err => {
      console.warn('[Onboarding] DB draft fetch failed (using localStorage):', err.message);
    });
  }, []); // eslint-disable-line

  // Persist every change — localStorage (instant) + DB (cross-device, debounced)
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        step: current, biz, products, agents, ccConfig,
      }));
    } catch (_) {}

    const timer = setTimeout(() => {
      console.log('[Onboarding] 💾 Saving draft to DB — step:', current, '| business:', biz.businessName || '(empty)');
      apiClient.post('/onboarding/save-draft', {
        step: current, biz, products, agents, ccConfig,
      }).then(() => {
        console.log('[Onboarding] Draft saved to DB ✅');
      }).catch(err => {
        console.warn('[Onboarding] Draft DB save failed:', err.message);
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [current, biz, products, agents, ccConfig]);

  // ── Step navigation ───────────────────────────────────────────────────────
  const steps = [
    { title: 'Your Business',   icon: <ShopOutlined /> },
    { title: 'Products & Goal', icon: <StarOutlined /> },
    { title: 'Your AI Team',    icon: <RobotOutlined /> },
    { title: 'Go Live',         icon: <RocketOutlined /> },
  ];

  // ── Product helpers ───────────────────────────────────────────────────────
  const addProduct = () =>
    setProducts(p => [...p, { name: '', description: '', price: '', currency: '₹', unit: 'per pack' }]);

  const removeProduct = (i) =>
    setProducts(p => p.filter((_, idx) => idx !== i));

  const updateProduct = (i, field, value) =>
    setProducts(p => p.map((item, idx) => idx === i ? { ...item, [field]: value } : item));

  // ── Generate agents ───────────────────────────────────────────────────────
  const generateAgents = async () => {
    if (!biz.businessName || !biz.businessType || !biz.businessDescription) {
      message.warning('Please fill in business name, type and description first');
      setCurrent(0);
      return;
    }
    console.log('[Onboarding] 🤖 Generating agents for:', biz.businessName, '|', biz.businessType, '| tone:', biz.brandTone);
    console.log('[Onboarding] Products being sent:', products.filter(p => p.name.trim()));
    setGenerating(true);
    try {
      const res = await apiClient.post('/onboarding/generate-agents', {
        ...biz,
        products: products.filter(p => p.name.trim()),
      });
      if (res.data.success) {
        console.log('[Onboarding] ✅ Agents generated:', res.data.agents.map(a => `${a.agentName} (${a.templateId})`));
        console.log('[Onboarding] Command Centre config:', res.data.commandCentreConfig);
        setAgents(res.data.agents);
        setCcConfig(res.data.commandCentreConfig);
        setCurrent(2);
      }
    } catch (err) {
      console.error('[Onboarding] ❌ Agent generation failed:', err.response?.data || err.message);
      message.error('Agent generation failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setGenerating(false);
    }
  };

  // ── Complete onboarding ───────────────────────────────────────────────────
  const completeOnboarding = async () => {
    console.log('[Onboarding] 🚀 Completing onboarding for:', biz.businessName);
    console.log('[Onboarding] Saving', agents.length, 'agents:', agents.map(a => a.templateId));
    setSaving(true);
    try {
      const res = await apiClient.post('/onboarding/complete', {
        ...biz,
        commandCentreGoal:    ccConfig?.goal || biz.goal,
        commandCentreActions: ccConfig?.actions || [],
        products:             products.filter(p => p.name.trim()),
        agents,
      });
      if (res.data.success) {
        console.log('[Onboarding] ✅ Done! Agents created:', res.data.agents);
        message.success(`🚀 You're live! ${res.data.agents.length} agents created.`);
        localStorage.removeItem(STORAGE_KEY);
        apiClient.post('/onboarding/save-draft', {}).catch(() => {}); // clear DB draft too
        setCurrent(3);
        setTimeout(() => onComplete?.(), 1500);
      }
    } catch (err) {
      message.error('Setup failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Agent card ────────────────────────────────────────────────────────────
  const AgentCard = ({ agent, index }) => {
    const isEditing = editingAgent === index;
    const color = ROLE_COLORS[agent.templateId?.split('_').slice(1).join('_')] || ROLE_COLORS.default;

    return (
      <Card
        size="small"
        style={{ marginBottom: 12, borderLeft: `4px solid ${color}` }}
        title={
          <Space>
            <RobotOutlined style={{ color }} />
            <Text strong>{agent.agentName}</Text>
            {agent.isDefault && <Tag color="green" style={{ fontSize: 10 }}>Default</Tag>}
          </Space>
        }
        extra={
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditingAgent(isEditing ? null : index)}
          >
            {isEditing ? 'Done' : 'Edit'}
          </Button>
        }
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          {agent.role}
        </Text>

        {isEditing ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text style={{ fontSize: 11, color: '#999' }}>AGENT NAME</Text>
              <Input
                size="small"
                value={agent.agentName}
                onChange={e => setAgents(prev => prev.map((a, i) =>
                  i === index ? { ...a, agentName: e.target.value } : a
                ))}
              />
            </div>
            <div>
              <Text style={{ fontSize: 11, color: '#999' }}>SYSTEM PROMPT</Text>
              <TextArea
                rows={5}
                size="small"
                value={agent.systemPrompt}
                onChange={e => setAgents(prev => prev.map((a, i) =>
                  i === index ? { ...a, systemPrompt: e.target.value } : a
                ))}
              />
            </div>
            <div>
              <Text style={{ fontSize: 11, color: '#999' }}>HANDOFF CONDITION</Text>
              <Input
                size="small"
                value={agent.handoffCondition}
                onChange={e => setAgents(prev => prev.map((a, i) =>
                  i === index ? { ...a, handoffCondition: e.target.value } : a
                ))}
              />
            </div>
          </Space>
        ) : (
          <div
            style={{ background: '#f8f9fa', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#555', maxHeight: 80, overflow: 'hidden', position: 'relative' }}
          >
            {agent.systemPrompt?.substring(0, 180)}…
          </div>
        )}
      </Card>
    );
  };

  // ── Step content ──────────────────────────────────────────────────────────
  const Step1 = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        message="Tell us about your business — we'll build your AI team automatically"
        type="info"
        showIcon
        icon={<ThunderboltOutlined />}
        style={{ marginBottom: 8 }}
      />
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Business Name *</Text>
            <Input
              placeholder="e.g. PetPotry"
              value={biz.businessName}
              onChange={e => setBiz(b => ({ ...b, businessName: e.target.value }))}
              size="large"
            />
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Business Type *</Text>
            <Select
              placeholder="Select type"
              value={biz.businessType || undefined}
              onChange={v => setBiz(b => ({ ...b, businessType: v }))}
              size="large"
              style={{ width: '100%' }}
              options={BUSINESS_TYPES.map(t => ({ value: t, label: t }))}
            />
          </div>
        </Col>
      </Row>

      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>
          Describe your business in 2-3 sentences *
        </Text>
        <TextArea
          rows={3}
          placeholder="e.g. PetPotry sells natural, human-grade pet food for dogs and cats. We ship across India with next-day delivery. Our customers are pet parents who care deeply about their pet's nutrition."
          value={biz.businessDescription}
          onChange={e => setBiz(b => ({ ...b, businessDescription: e.target.value }))}
        />
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: 10 }}>Brand Tone *</Text>
        <Row gutter={12}>
          {TONE_OPTIONS.map(t => (
            <Col xs={24} sm={12} key={t.value} style={{ marginBottom: 8 }}>
              <Card
                size="small"
                hoverable
                onClick={() => setBiz(b => ({ ...b, brandTone: t.value }))}
                style={{
                  cursor: 'pointer',
                  borderColor: biz.brandTone === t.value ? '#1890ff' : '#f0f0f0',
                  background: biz.brandTone === t.value ? '#e6f7ff' : '#fff',
                }}
              >
                <Text strong style={{ fontSize: 13 }}>{t.label}</Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{t.desc}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <Button
        type="primary"
        size="large"
        block
        disabled={!biz.businessName || !biz.businessType || !biz.businessDescription}
        onClick={() => setCurrent(1)}
      >
        Next: Products & Goal →
      </Button>
    </Space>
  );

  const Step2 = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Products / Services
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          Add your key products — the AI will mention them naturally in conversations.
        </Text>
        {products.map((p, i) => (
          <Card size="small" key={i} style={{ marginBottom: 8 }} bodyStyle={{ padding: '10px 12px' }}>
            <Row gutter={8} align="middle">
              <Col flex="auto">
                <Row gutter={8}>
                  <Col xs={24} sm={10}>
                    <Input
                      placeholder="Product name *"
                      value={p.name}
                      onChange={e => updateProduct(i, 'name', e.target.value)}
                      size="small"
                    />
                  </Col>
                  <Col xs={24} sm={10}>
                    <Input
                      placeholder="Short description"
                      value={p.description}
                      onChange={e => updateProduct(i, 'description', e.target.value)}
                      size="small"
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <Input
                      placeholder="Price"
                      value={p.price}
                      onChange={e => updateProduct(i, 'price', e.target.value)}
                      size="small"
                      prefix={p.currency}
                    />
                  </Col>
                  <Col xs={24} sm={6}>
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={p.unit || 'per pack'}
                      onChange={val => updateProduct(i, 'unit', val)}
                      dropdownMatchSelectWidth={false}
                      options={[
                        { value: 'per pack',      label: 'per pack' },
                        { value: 'per kg',        label: 'per kg' },
                        { value: 'per 500g',      label: 'per 500g' },
                        { value: 'per piece',     label: 'per piece' },
                        { value: 'per litre',     label: 'per litre' },
                        { value: 'per month',     label: 'per month' },
                        { value: 'per year',      label: 'per year' },
                        { value: 'per session',   label: 'per session' },
                        { value: 'per visit',     label: 'per visit' },
                        { value: 'per person',    label: 'per person' },
                        { value: 'per night',     label: 'per night' },
                        { value: 'per sq ft',     label: 'per sq ft' },
                        { value: 'custom',        label: 'custom…' },
                      ]}
                    />
                    {p.unit === 'custom' && (
                      <Input
                        size="small"
                        placeholder="e.g. per 2kg bag"
                        style={{ marginTop: 4 }}
                        value={p.customUnit || ''}
                        onChange={e => updateProduct(i, 'customUnit', e.target.value)}
                      />
                    )}
                  </Col>
                </Row>
              </Col>
              {products.length > 1 && (
                <Col>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeProduct(i)}
                  />
                </Col>
              )}
            </Row>
          </Card>
        ))}
        <Button icon={<PlusOutlined />} size="small" onClick={addProduct} style={{ marginTop: 4 }}>
          Add Product
        </Button>
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>
          What is your #1 goal with WhatsApp AI?
        </Text>
        <Input
          placeholder="e.g. Increase repeat purchases and reduce cart abandonment"
          value={biz.goal}
          onChange={e => setBiz(b => ({ ...b, goal: e.target.value }))}
          size="large"
        />
      </div>

      <Row gutter={12}>
        <Col span={12}>
          <Button block size="large" onClick={() => setCurrent(0)}>← Back</Button>
        </Col>
        <Col span={12}>
          <Button
            type="primary"
            block
            size="large"
            loading={generating}
            onClick={generateAgents}
            icon={<RobotOutlined />}
          >
            {generating ? 'Building your AI team…' : 'Generate My Agents →'}
          </Button>
        </Col>
      </Row>
    </Space>
  );

  const Step3 = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {generating ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Title level={5}>Building your AI team…</Title>
            <Text type="secondary">Reading your business description and crafting each agent's personality</Text>
          </div>
        </div>
      ) : (
        <>
          <Alert
            message={`${agents.length} agents created for ${biz.businessName} — review and edit before going live`}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />

          {agents.map((agent, i) => AgentCard({ agent, index: i, key: i }))}

          {ccConfig && (
            <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
              <Space>
                <ThunderboltOutlined style={{ color: '#faad14' }} />
                <Text strong>Command Centre configured:</Text>
              </Space>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                Goal: {ccConfig.goal}
              </Text>
              <div style={{ marginTop: 6 }}>
                {(ccConfig.actions || []).map(a => (
                  <Tag key={a} style={{ fontSize: 11, marginBottom: 4 }}>{a}</Tag>
                ))}
              </div>
            </Card>
          )}

          <Row gutter={12}>
            <Col span={8}>
              <Button block onClick={() => setCurrent(1)}>← Back</Button>
            </Col>
            <Col span={16}>
              <Button
                type="primary"
                block
                size="large"
                loading={saving}
                icon={<RocketOutlined />}
                onClick={completeOnboarding}
              >
                {saving ? 'Setting up…' : "Looks good — Go Live! 🚀"}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Space>
  );

  const Step4 = () => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
      <Title level={3}>You're live, {biz.businessName}! 🎉</Title>
      <Paragraph type="secondary">
        Your AI team is active. Test it in Live Test, connect your WhatsApp number in Settings,
        and watch Command Centre for growth opportunities.
      </Paragraph>
      <Space>
        <Tag color="green">✅ {agents.length} Agents Active</Tag>
        <Tag color="blue">✅ Command Centre Configured</Tag>
        <Tag color="purple">✅ Signal Tracking On</Tag>
      </Space>
    </div>
  );

  const stepContent = [Step1(), Step2(), Step3(), Step4()];

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <HeartOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
        <Title level={2} style={{ marginBottom: 4 }}>Welcome! Let's set you up</Title>
        <Text type="secondary">
          Answer a few questions — we'll build your entire AI assistant stack automatically.
        </Text>
      </div>

      <Steps
        current={current}
        items={steps}
        style={{ marginBottom: 32 }}
        size="small"
      />

      <Card>
        {stepContent[current]}
      </Card>
    </div>
  );
};

export default Onboarding;
